import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  IconButton,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, ExpandMore, Info } from '@mui/icons-material';
import axios from 'axios';
import RuleBuilder from '../components/RuleBuilder';
import { conditionsToJson, jsonToConditions } from '../utils/ruleConverter';

const ImportRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    sourceSystem: '',
    targetSystem: '',
    conditions: [],
    actions: [],
    priority: 0,
    isActive: true
  });

  const sourceSystemOptions = [
    { value: 'shopify', label: 'Shopify' },
    { value: 'metakocka', label: 'MetaKocka' }
  ];

  const targetSystemOptions = [
    { value: 'metakocka', label: 'MetaKocka' },
    { value: 'eliz', label: 'Eliz' }
  ];

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/rules/import');
      console.log('Import rules response:', response.data);
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      setError('Failed to load import rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleCreateRule = () => {
    setSelectedRule(null);
    setRuleForm({
      name: '',
      description: '',
      sourceSystem: '',
      targetSystem: '',
      conditions: [{ field: '', operator: '', value: '', connector: 'and' }],
      actions: [{ type: 'copy_product', sourceField: '', targetField: '' }],
      priority: 0,
      isActive: true
    });
    setShowForm(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    
    // Convert stored conditions/actions to the format expected by RuleBuilder
    const conditions = rule.conditions ? jsonToConditions(rule.conditions, 'import') : [{ field: '', operator: '', value: '', connector: 'and' }];
    const actions = rule.actions && Array.isArray(rule.actions) 
      ? rule.actions 
      : rule.actions 
        ? [rule.actions] 
        : [{ type: 'copy_product', sourceField: '', targetField: '' }];
    
    setRuleForm({
      name: rule.name || '',
      description: rule.description || '',
      sourceSystem: rule.source_system || '',
      targetSystem: rule.target_system || '',
      conditions: conditions,
      actions: actions,
      priority: rule.priority || 0,
      isActive: rule.is_active !== false
    });
    setShowForm(true);
  };

  const handleSaveRule = async () => {
    try {
      setError('');
      setSuccess('');
      
      const ruleData = {
        name: ruleForm.name,
        description: ruleForm.description,
        source_system: ruleForm.sourceSystem,
        target_system: ruleForm.targetSystem,
        conditions: conditionsToJson(ruleForm.conditions, 'import'),
        actions: ruleForm.actions,
        priority: ruleForm.priority,
        is_active: ruleForm.isActive
      };

      if (selectedRule) {
        await axios.put(`/api/rules/import/${selectedRule.id}`, ruleData);
        setSuccess('Import rule updated successfully');
      } else {
        await axios.post('/api/rules/import', ruleData);
        setSuccess('Import rule created successfully');
      }

      setShowForm(false);
      fetchRules();
      
    } catch (error) {
      console.error('Save rule error:', error);
      setError('Failed to save import rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this import rule?')) {
      return;
    }

    try {
      setError('');
      await axios.delete(`/api/rules/import/${ruleId}`);
      setSuccess('Import rule deleted successfully');
      fetchRules();
    } catch (error) {
      console.error('Delete rule error:', error);
      setError('Failed to delete import rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleRule = async (ruleId, isActive) => {
    try {
      setError('');
      await axios.patch(`/api/rules/import/${ruleId}`, { is_active: !isActive });
      setSuccess(`Import rule ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchRules();
    } catch (error) {
      console.error('Toggle rule error:', error);
      setError('Failed to toggle import rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#ffffff' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'priority', 
      headerName: 'Priority', 
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#ffffff' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'source_system', 
      headerName: 'Source', 
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#ffffff' }}>
          {sourceSystemOptions.find(s => s.value === params.value)?.label || params.value}
        </Typography>
      )
    },
    { 
      field: 'target_system', 
      headerName: 'Target', 
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#ffffff' }}>
          {targetSystemOptions.find(s => s.value === params.value)?.label || params.value}
        </Typography>
      )
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={`Click to ${params.value ? 'deactivate' : 'activate'}`}>
          <FormControlLabel
            control={
              <Switch
                checked={params.value}
                onChange={() => handleToggleRule(params.row.id, params.value)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ color: params.value ? '#4caf50' : 'rgba(255, 255, 255, 0.6)' }}>
                {params.value ? 'Active' : 'Inactive'}
              </Typography>
            }
          />
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit Rule">
            <IconButton 
              size="small" 
              onClick={() => handleEditRule(params.row)}
              sx={{ 
                color: '#64b5f6',
                '&:hover': { 
                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                  color: '#90caf9'
                }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Rule">
            <IconButton 
              size="small" 
              onClick={() => handleDeleteRule(params.row.id)}
              sx={{ 
                color: '#f48fb1',
                '&:hover': { 
                  backgroundColor: 'rgba(244, 143, 177, 0.1)',
                  color: '#f8bbd9'
                }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Information Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: '#ffffff' }} />}
            aria-controls="import-rules-info-content"
            id="import-rules-info-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              <Typography variant="h6" sx={{ color: '#ffffff' }}>How Import Rules Work</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Import Rules</strong> automate the process of importing and transforming product data between different systems.
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>How it works:</strong> When products are imported from a source system, these rules determine how the data should be transformed and mapped to the target system.
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Source Systems:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, color: 'rgba(255, 255, 255, 0.85)' }}>
              <li><strong>Shopify:</strong> E-commerce platform product data</li>
              <li><strong>MetaKocka:</strong> ERP system product information</li>
            </Box>
            <Typography variant="body2" paragraph sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Target Systems:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, color: 'rgba(255, 255, 255, 0.85)' }}>
              <li><strong>MetaKocka:</strong> Store processed product data in ERP</li>
              <li><strong>Eliz:</strong> Send product information to fulfillment system</li>
            </Box>
            <Typography variant="body2" paragraph sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Rule Priority:</strong> Rules are processed in priority order. Lower numbers = higher priority.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#ffffff' }}>
            Import Rules
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchRules}
              sx={{ mr: 1 }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateRule}
            >
              Create Import Rule
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rules}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                color: '#ffffff',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#ffffff',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                color: '#ffffff',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          />
        </Box>

        {/* Rule Form Dialog */}
        <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ color: '#ffffff' }}>
            {selectedRule ? 'Edit Import Rule' : 'Create Import Rule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rule Name"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Priority"
                  type="number"
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Source System</InputLabel>
                  <Select
                    value={ruleForm.sourceSystem}
                    onChange={(e) => setRuleForm({ ...ruleForm, sourceSystem: e.target.value })}
                    label="Source System"
                    required
                  >
                    {sourceSystemOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Target System</InputLabel>
                  <Select
                    value={ruleForm.targetSystem}
                    onChange={(e) => setRuleForm({ ...ruleForm, targetSystem: e.target.value })}
                    label="Target System"
                    required
                  >
                    {targetSystemOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <RuleBuilder
                  value={ruleForm.conditions}
                  onChange={(conditions) => setRuleForm({ ...ruleForm, conditions })}
                  ruleType="import"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSaveRule} variant="contained">
              {selectedRule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ImportRulesPage;